import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Link, Stack, TextField, Typography,
} from '@mui/material';
import {
  assessCompletenessApi,
  assignInspectionApi,
  counterBriefApi,
  explainFeesApi,
  explainZoningApi,
  permitPortfolioApi,
  planCorrectionsApi,
  prepareInspectionApi,
  statusCoachApi,
  suggestInspectionAssignmentApi,
} from '../../api/applicationType.api';
import {
  civicActionAllowed,
  continuumPhaseLabel,
  resolveContinuumPhase,
} from '../../utils/continuumPhase';
import {
  friendlyCityLabel,
  friendlyPhaseChip,
  friendlyToolLabel,
} from '../../utils/citizenFriendlyLabels';

const ACCENT = '#1565c0';

/** Next Mon–Fri local calendar date for roster availability checks. */
function nextWeekdayPreferredDate(from = new Date()) {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  for (let i = 0; i < 8; i += 1) {
    const wd = d.getDay();
    if (wd >= 1 && wd <= 5) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
    d.setDate(d.getDate() + 1);
  }
  return from.toISOString().slice(0, 10);
}

/**
 * Package-agnostic civic CLAW coaches (completeness, fees, counter, status, zoning, corrections).
 * Always uses session.packageId / packageId prop — never hard-codes a city.
 * Inspection + post-submit tools unlock only after the LNP application is filed.
 */
export default function CitizenCivicClawPanel({
  sessionId,
  packageId,
  session,
  applicationStatus = null,
  compact = false,
  mode = 'citizen', // citizen | staff
}) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [zoningUse, setZoningUse] = useState('');
  const [deficiency, setDeficiency] = useState('');

  const pkg = packageId
    || session?.packageId
    || session?.handoffPacket?.jurisdictionId
    || null;

  const phase = useMemo(
    () => resolveContinuumPhase({ session, applicationStatus }),
    [session, applicationStatus],
  );
  const can = (action) => civicActionAllowed(action, phase, { mode });

  if (!sessionId && mode !== 'zoning-only') return null;

  const run = async (key, fn) => {
    if (!can(key)) {
      setError(
        key.startsWith('inspect') || key === 'corrections'
          ? 'Submit the application before using this helper'
          : 'Start an application draft before using this helper',
      );
      return;
    }
    setBusy(key);
    setError(null);
    try {
      const data = await fn();
      setResult({ key, data });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Could not run that helper');
    } finally {
      setBusy(null);
    }
  };

  const isStaff = mode === 'staff';
  const assigned = session?.meta?.assignedInspector
    || result?.data?.assignment
    || (result?.key === 'inspectAssign' ? result?.data?.assigned : null);

  return (
    <Box
      sx={{
        bgcolor: compact ? 'transparent' : '#fff',
        borderRadius: compact ? 0 : 3,
        border: compact ? 'none' : '1px solid rgba(12,26,46,0.08)',
        p: compact ? 0 : 2,
      }}
      data-testid="citizen-civic-claw"
      data-continuum-phase={phase}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2" fontWeight={700}>
          {isStaff ? 'Planner helpers' : 'Help with your application'}
        </Typography>
        {pkg && (
          <Chip
            size="small"
            label={isStaff ? pkg : friendlyCityLabel(pkg)}
            sx={isStaff ? { fontFamily: 'monospace', fontSize: 11 } : undefined}
          />
        )}
        <Chip
          size="small"
          color={phase === 'submitted' ? 'success' : phase === 'draft' ? 'info' : 'default'}
          label={isStaff ? phase : friendlyPhaseChip(phase)}
          data-testid="civic-claw-phase"
        />
        {assigned?.name && (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label={`Inspector: ${assigned.name}`}
            data-testid="civic-claw-assigned-inspector"
          />
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {isStaff
          ? (phase === 'intake'
            ? 'Create a draft to unlock inspector suggest/assign'
            : continuumPhaseLabel(phase))
          : continuumPhaseLabel(phase)}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {sessionId && (
          <>
            <Button
              size="small"
              variant={mode === 'staff' ? 'contained' : 'outlined'}
              disabled={Boolean(busy) || !can('completeness')}
              onClick={() => run('completeness', () => assessCompletenessApi(sessionId, { packageId: pkg }))}
              sx={{ textTransform: 'none' }}
              data-testid="civic-claw-completeness"
            >
              {busy === 'completeness' ? <CircularProgress size={14} /> : 'Completeness'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={Boolean(busy) || !pkg || !can('fees')}
              onClick={() => run('fees', () => explainFeesApi({ sessionId, packageId: pkg }))}
              sx={{ textTransform: 'none' }}
              data-testid="civic-claw-fees"
            >
              {busy === 'fees' ? <CircularProgress size={14} /> : 'Explain fees'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={Boolean(busy) || !can('counter')}
              onClick={() => run('counter', () => counterBriefApi(sessionId, { packageId: pkg }))}
              sx={{ textTransform: 'none' }}
              data-testid="civic-claw-counter"
            >
              {busy === 'counter' ? <CircularProgress size={14} /> : 'Counter brief'}
            </Button>
            {can('status') && (
              <Button
                size="small"
                variant="outlined"
                disabled={Boolean(busy)}
                onClick={() => run('status', () => statusCoachApi(sessionId))}
                sx={{ textTransform: 'none' }}
                data-testid="civic-claw-status"
              >
                {busy === 'status' ? <CircularProgress size={14} /> : 'Status coach'}
              </Button>
            )}
            {can('inspectSuggest') && (
              <Button
                size="small"
                variant="contained"
                disabled={Boolean(busy) || !pkg}
                onClick={() => run('inspectSuggest', () => suggestInspectionAssignmentApi({
                  sessionId,
                  packageId: pkg,
                  inspectionType: 'building',
                  preferredDate: nextWeekdayPreferredDate(),
                }))}
                sx={{ textTransform: 'none' }}
                data-testid="civic-claw-inspect-suggest"
              >
                {busy === 'inspectSuggest' ? <CircularProgress size={14} /> : 'Suggest inspector'}
              </Button>
            )}
            {can('inspectPrep') && (
              <Button
                size="small"
                variant="outlined"
                disabled={Boolean(busy)}
                onClick={() => run('inspectPrep', () => prepareInspectionApi(sessionId, { packageId: pkg }))}
                sx={{ textTransform: 'none' }}
                data-testid="civic-claw-inspect-prep"
              >
                {busy === 'inspectPrep' ? <CircularProgress size={14} /> : 'Inspection prep'}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              disabled={Boolean(busy) || !can('portfolio')}
              onClick={() => run('portfolio', () => permitPortfolioApi(sessionId, { packageId: pkg }))}
              sx={{ textTransform: 'none' }}
              data-testid="civic-claw-portfolio"
            >
              {busy === 'portfolio' ? <CircularProgress size={14} /> : (isStaff ? 'Permit portfolio' : 'Permit path')}
            </Button>
          </>
        )}
      </Stack>

      {!isStaff && phase !== 'submitted' && (
        <Alert severity="info" sx={{ mb: 1, py: 0 }} data-testid="civic-claw-inspect-locked">
          Inspection prep unlocks after you
          {' '}
          <strong>submit</strong>
          {' '}
          the application
          {phase === 'draft' ? ' (your draft is ready — submit from the receipt below).' : '.'}
          {' '}
          A planner assigns the inspector from the staff desk.
        </Alert>
      )}
      {isStaff && phase === 'intake' && (
        <Alert severity="info" sx={{ mb: 1, py: 0 }} data-testid="civic-claw-inspect-locked">
          Suggest / assign unlocks after an LNP draft exists on this session.
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }} alignItems="flex-start">
        <TextField
          size="small"
          fullWidth
          placeholder={pkg
            ? `Proposed use for ${friendlyCityLabel(pkg)} zoning…`
            : 'Describe the proposed use for zoning help'}
          value={zoningUse}
          onChange={(e) => setZoningUse(e.target.value)}
          disabled={Boolean(busy) || !pkg || !can('zoning')}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={Boolean(busy) || !pkg || !zoningUse.trim() || !can('zoning')}
          onClick={() => run('zoning', () => explainZoningApi({
            packageId: pkg,
            proposedUse: zoningUse.trim(),
            address: session?.intakeFields?.projectAddress || session?.formPrefill?.address,
            zoningCode: session?.parcel?.zoning?.code || session?.parcel?.zoning?.zoning,
          }))}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap', borderColor: ACCENT, color: ACCENT }}
          data-testid="civic-claw-zoning"
        >
          {busy === 'zoning' ? <CircularProgress size={14} /> : 'Explain zoning'}
        </Button>
      </Stack>

      {sessionId && can('corrections') && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
          <TextField
            size="small"
            fullWidth
            placeholder="Agency deficiency / bounce note…"
            value={deficiency}
            onChange={(e) => setDeficiency(e.target.value)}
            disabled={Boolean(busy)}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={Boolean(busy) || !deficiency.trim()}
            onClick={() => run('corrections', () => planCorrectionsApi(sessionId, {
              message: deficiency.trim(),
            }))}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            data-testid="civic-claw-corrections"
          >
            {busy === 'corrections' ? <CircularProgress size={14} /> : 'Plan corrections'}
          </Button>
        </Stack>
      )}

      {result?.data && (
        <Box sx={{ mt: 1.25, p: 1.25, bgcolor: 'rgba(21,101,192,0.05)', borderRadius: 2 }}>
          <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
            {isStaff
              ? (result.data.claw?.tool || result.key)
              : friendlyToolLabel(result.data.claw?.tool || result.key)}
            {result.data.packageId
              ? ` · ${isStaff ? result.data.packageId : friendlyCityLabel(result.data.packageId)}`
              : ''}
          </Typography>
          {result.data.summary && (
            <Typography variant="body2" sx={{ mb: 0.75 }}>{result.data.summary}</Typography>
          )}
          {result.data.plainLanguage && (
            <Typography variant="body2" sx={{ mb: 0.75 }}>{result.data.plainLanguage}</Typography>
          )}
          {result.data.verdict && (
            <Alert
              severity={result.data.verdict === 'ready_for_plan_check' ? 'success' : 'warning'}
              sx={{ mb: 1, py: 0 }}
            >
              {result.data.verdict}
              {' '}
              · score
              {' '}
              {result.data.score}
            </Alert>
          )}
          {(result.data.bounceReasons || result.data.actions || []).slice(0, 8).map((b) => (
            <Typography key={`${b.code}-${b.message}`} variant="body2" sx={{ mb: 0.35 }}>
              •
              {' '}
              {b.message}
              {b.recommendedTool ? ` → ${b.recommendedTool}` : ''}
              {b.citizenAction || b.fixHint ? ` (${b.citizenAction || b.fixHint})` : ''}
            </Typography>
          ))}
          {(result.data.sayThis || []).map((t) => (
            <Typography key={t} variant="body2" sx={{ mb: 0.35 }}>
              •
              {' '}
              {t}
            </Typography>
          ))}
          {(result.data.whatHappensNext || []).map((t) => (
            <Typography key={t} variant="body2" sx={{ mb: 0.35 }}>
              →
              {' '}
              {t}
            </Typography>
          ))}
          {(result.data.lines || []).map((li) => (
            <Typography key={li.applicationTypeId || li.label} variant="body2" sx={{ mb: 0.35 }}>
              {li.plainLanguage || `${li.label}: $${li.total}`}
            </Typography>
          ))}
          {result.data.recommendation && (
            <Alert severity="success" sx={{ mb: 1, py: 0 }}>
              Recommend
              {' '}
              <strong>{result.data.recommendation.name}</strong>
              {' '}
              (score
              {' '}
              {result.data.recommendation.score}
              )
              {result.key === 'inspectSuggest' && sessionId && pkg && can('inspectAssign') && (
                <Button
                  size="small"
                  sx={{ ml: 1, textTransform: 'none' }}
                  disabled={Boolean(busy)}
                  onClick={() => run('inspectAssign', () => assignInspectionApi({
                    sessionId,
                    packageId: pkg,
                    inspectorId: result.data.recommendation.inspectorId,
                    inspectionType: 'building',
                  }))}
                >
                  Confirm assign
                </Button>
              )}
            </Alert>
          )}
          {(result.data.ranked || []).slice(0, 4).map((r) => (
            <Typography key={r.inspectorId} variant="body2" sx={{ mb: 0.25 }}>
              {r.eligible ? '✓' : '·'}
              {' '}
              {r.name}
              {' '}
              ·
              {r.score}
              {r.distanceKm != null ? ` · ${r.distanceKm.toFixed(1)}km` : ''}
            </Typography>
          ))}
          {(result.data.items || []).map((it) => (
            <Typography key={it.id} variant="body2" sx={{ mb: 0.25 }}>
              {it.required ? '●' : '○'}
              {' '}
              {it.label}
            </Typography>
          ))}
          {(result.data.steps || []).map((s) => (
            <Typography key={s.applicationTypeId} variant="body2" sx={{ mb: 0.25 }}>
              {s.order}.
              {' '}
              {s.label}
              {s.role ? ` (${s.role})` : ''}
            </Typography>
          ))}
          {(result.data.citations || []).slice(0, 3).map((c) => (
            <Typography key={c.id || c.url} variant="caption" color="text.secondary" display="block">
              {c.url ? <Link href={c.url} target="_blank" rel="noopener noreferrer">{c.title}</Link> : c.title}
            </Typography>
          ))}
          {result.data.disclaimer && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              {result.data.disclaimer}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
